// CORS Image Proxy - for external images that don't support CORS
const IMAGE_PROXY_URL = 'https://app.bonndle.com/api/image-proxy';

// Domains that don't need proxying (already support CORS)
const CORS_SAFE_DOMAINS = [
  'images.unsplash.com',
  'static.wixstatic.com',
  'api.dicebear.com',
  'app.bonndle.com',
  'localhost',
  '127.0.0.1',
];

/**
 * Wrap an image URL with the CORS proxy if needed
 * @param {string} url - The image URL
 * @returns {string} - The proxied URL or original URL if safe
 */
export function proxyImageUrl(url) {
  if (!url || typeof url !== 'string') return url;

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;

    // Check if the domain is CORS-safe
    const isSafe = CORS_SAFE_DOMAINS.some(domain =>
      hostname === domain || hostname.endsWith('.' + domain)
    );

    if (isSafe) {
      return url;
    }

    // Use proxy for external URLs
    return `${IMAGE_PROXY_URL}?url=${encodeURIComponent(url)}`;
  } catch {
    // If URL parsing fails, return as-is
    return url;
  }
}

// Niche category images
export const NICHE_IMAGES = {
  foodies: [
    'https://images.unsplash.com/photo-1542838132-92c53300491e?w=500&q=80',
    'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=500&q=80',
  ],
  tech: [
    'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=500&q=80',
    'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=500&q=80',
  ],
  internet: [
    'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=500&q=80',
    'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=500&q=80',
  ],
  bills: [
    'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=500&q=80',
    'https://images.unsplash.com/photo-1583521214690-73421a1829a9?w=500&q=80',
  ],
  healthcare: [
    'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=500&q=80',
    'https://images.unsplash.com/photo-1576091160550-2173dad99961?w=500&q=80',
  ],
  gaming: [
    'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=500&q=80',
    'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=500&q=80',
  ],
  education: [
    'https://images.unsplash.com/photo-1523050335102-c89b1c970726?w=500&q=80',
    'https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=500&q=80',
  ],
  entertainment: [
    'https://images.unsplash.com/photo-1522869635100-9f4c5e86aa37?w=500&q=80',
    'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=500&q=80',
  ],
  general: ['https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=500&q=80'],
};

// Groups list (createdAt in ISO format, most recent first for demo)
export const GROUPS_LIST = [
  { id: 9, name: 'MoneyMint Team', members: 15, niche: 'mixed', status: 'Private', createdAt: '2026-04-05T10:00:00Z' },
  { id: 5, name: 'BillSavers', members: 1100, niche: 'bills', status: 'Public', createdAt: '2026-04-01T14:30:00Z' },
  { id: 3, name: 'Tech Squad', members: 12, niche: 'tech', status: 'Public', createdAt: '2026-03-28T09:15:00Z' },
  { id: 1, name: 'Weekend Foodies', members: 8, niche: 'foodies', status: 'Public', createdAt: '2026-03-20T16:45:00Z' },
  { id: 2, name: 'Family Savings', members: 4, niche: 'general', status: 'Private', createdAt: '2026-03-15T11:00:00Z' },
  { id: 4, name: 'Bonndle Internet', members: 250, niche: 'internet', status: 'Public', createdAt: '2026-03-10T08:00:00Z' },
  { id: 6, name: 'HealthFam', members: 42, niche: 'healthcare', status: 'Public', createdAt: '2026-03-05T13:20:00Z' },
  { id: 7, name: 'GameOnn', members: 89, niche: 'gaming', status: 'Public', createdAt: '2026-02-28T19:00:00Z' },
  { id: 8, name: 'KAKUStudents', members: 450, niche: 'education', status: 'Public', createdAt: '2026-02-20T07:30:00Z' },
];

// Teams list - workplace/organization teams the user belongs to
export const TEAMS_LIST = [
  {
    id: 'team-1',
    name: 'MoneyMint Engineering',
    organization: 'MoneyMint Inc.',
    members: 24,
    niche: 'tech',
    role: 'Member',
    joinedAt: '2024-06-15',
    description: 'Engineering team coordinating on tech purchases and software subscriptions',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=ME',
  },
  {
    id: 'team-2',
    name: 'MoneyMint All Hands',
    organization: 'MoneyMint Inc.',
    members: 89,
    niche: 'general',
    role: 'Member',
    joinedAt: '2024-06-15',
    description: 'Company-wide team for organization benefits and group deals',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=MA',
  },
  {
    id: 'team-3',
    name: 'Downtown Fitness Club',
    organization: 'Fitness Club',
    members: 156,
    niche: 'healthcare',
    role: 'Admin',
    joinedAt: '2024-03-10',
    description: 'Local gym members pooling for supplements and equipment deals',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=DF',
  },
];

// Deals by group
export const INITIAL_ISOLATED_DEALS = {
  1: [
    {
      id: 'deal-1-1',
      groupId: 1,
      brand: 'FoodSpot',
      logo: 'FS',
      color: 'bg-orange-500',
      offer: '3% Cashback',
      price: '$35',
      discount: '5% OFF',
      groupSize: '12/20 Joined',
      desc: 'On group orders over $30',
      image: NICHE_IMAGES.foodies[0],
      terms: 'Join the Weekend Foodies. Buy together, save on delivery and get 3% back.',
    },
    {
      id: 'deal-1-2',
      groupId: 1,
      brand: 'Whole Foods',
      logo: 'WF',
      color: 'bg-green-700',
      offer: '10% Cashback',
      price: '$120',
      discount: '10% OFF',
      groupSize: '6/10 Joined',
      desc: 'Weekly grocery haul',
      image: NICHE_IMAGES.foodies[1],
      terms: 'Unlock 10% collective cashback when 10 members commit to the weekly haul.',
    },
  ],
  2: [
    {
      id: 'deal-2-1',
      groupId: 2,
      brand: 'Amazon',
      logo: 'AZ',
      color: 'bg-black',
      offer: '3% Cashback',
      price: '$85',
      discount: '2% OFF',
      groupSize: '3/5 Joined',
      desc: 'Prime group restock',
      image: NICHE_IMAGES.general[0],
      terms: 'Family restock event. Earn shared cashback on everyday essentials.',
    },
  ],
  3: [
    {
      id: 'deal-3-1',
      groupId: 3,
      brand: 'Apple',
      logo: 'AP',
      color: 'bg-zinc-800',
      offer: '3% Cashback',
      price: '$499',
      discount: '3% OFF',
      groupSize: '1/3 Joined',
      desc: 'Collective hardware upgrades',
      image: NICHE_IMAGES.tech[0],
      terms: 'The Tech Squad is upgrading! Join to secure collective hardware rewards.',
    },
    {
      id: 'deal-3-2',
      groupId: 3,
      brand: 'TechNova',
      logo: 'TN',
      color: 'bg-blue-600',
      offer: '15% Off',
      price: '$59',
      discount: '15% OFF',
      groupSize: '4/5 Joined',
      desc: 'Group gadget deals',
      image: NICHE_IMAGES.tech[1],
      terms: 'Unlock 15% OFF when 5 members commit. Chat with the Tech Squad to coordinate.',
    },
  ],
  4: [
    {
      id: 'deal-4-1',
      groupId: 4,
      brand: 'Starlink',
      logo: 'SL',
      color: 'bg-blue-900',
      offer: '3% Cashback',
      price: '$120',
      discount: '3% OFF',
      groupSize: '8/10 Joined',
      desc: 'Satellite internet collective',
      image: NICHE_IMAGES.internet[0],
      terms: 'Pay via Bonndle to unlock shared 3% cashback for the entire neighborhood group.',
      category: 'internet',
    },
    {
      id: 'deal-4-2',
      groupId: 4,
      brand: 'Xfinity',
      logo: 'XF',
      color: 'bg-red-600',
      offer: '5% Cashback',
      price: '$75',
      discount: '5% OFF',
      groupSize: '15/20 Joined',
      desc: 'Gigabit speeds commit',
      image: NICHE_IMAGES.internet[1],
      terms: 'Standard monthly billing via Bonndle App triggers a 5% cashback reward.',
      category: 'internet',
    },
    {
      id: 'deal-4-3',
      groupId: 4,
      brand: 'AT&T Fiber',
      logo: 'AT',
      color: 'bg-sky-600',
      offer: '4% Cashback',
      price: '$65',
      discount: '4% OFF',
      groupSize: '22/30 Joined',
      desc: 'Fiber collective savings',
      image: NICHE_IMAGES.internet[0],
      terms: 'Group fiber subscribers get automatic 4% cashback on monthly bills.',
      category: 'internet',
    },
    {
      id: 'deal-4-4',
      groupId: 4,
      brand: 'Google Fiber',
      logo: 'GF',
      color: 'bg-green-600',
      offer: '6% Cashback',
      price: '$100',
      discount: '6% OFF',
      groupSize: '5/15 Joined',
      desc: 'Premium fiber group',
      image: NICHE_IMAGES.internet[1],
      terms: 'Unlock premium cashback rates when 15 members commit to Google Fiber.',
      category: 'internet',
    },
  ],
  5: [
    {
      id: 'deal-5-1',
      groupId: 5,
      brand: 'Duke Energy',
      logo: 'DE',
      color: 'bg-emerald-600',
      offer: '3% Cashback',
      price: '$150',
      discount: '2% OFF',
      groupSize: '45/100 Joined',
      desc: 'Power savers drive',
      image: NICHE_IMAGES.bills[0],
      terms: 'Pay your utility bill via Bonndle. Every 100 members paying through the group unlocks 3% back.',
      category: 'bills',
    },
    {
      id: 'deal-5-2',
      groupId: 5,
      brand: 'Water Works',
      logo: 'WW',
      color: 'bg-blue-400',
      offer: '5% Cashback',
      price: '$45',
      discount: '5% OFF',
      groupSize: '12/25 Joined',
      desc: 'Water usage collective',
      image: NICHE_IMAGES.bills[1],
      terms: 'Coordinated water utility payments via the BillSavers portal.',
      category: 'bills',
    },
    {
      id: 'deal-5-3',
      groupId: 5,
      brand: 'National Grid',
      logo: 'NG',
      color: 'bg-orange-600',
      offer: '4% Cashback',
      price: '$180',
      discount: '4% OFF',
      groupSize: '78/150 Joined',
      desc: 'Gas & electric savings',
      image: NICHE_IMAGES.bills[0],
      terms: 'Combined gas and electric bill payments for maximum group savings.',
      category: 'bills',
    },
    {
      id: 'deal-5-4',
      groupId: 5,
      brand: 'Waste Management',
      logo: 'WM',
      color: 'bg-green-700',
      offer: '3% Cashback',
      price: '$35',
      discount: '3% OFF',
      groupSize: '30/50 Joined',
      desc: 'Trash & recycling group',
      image: NICHE_IMAGES.bills[1],
      terms: 'Neighborhood waste management collective for quarterly bill savings.',
      category: 'bills',
    },
  ],
  6: [
    {
      id: 'deal-6-1',
      groupId: 6,
      brand: 'Teladoc',
      logo: 'TD',
      color: 'bg-rose-500',
      offer: '10% Cashback',
      price: '$45',
      discount: '10% OFF',
      groupSize: '5/10 Joined',
      desc: 'Group telehealth credits',
      image: NICHE_IMAGES.healthcare[0],
      terms: 'Secure group rates for 24/7 doctor access. Cashback applied to all active HealthFam members.',
    },
    {
      id: 'deal-6-2',
      groupId: 6,
      brand: 'CVS Pharmacy',
      logo: 'CV',
      color: 'bg-red-600',
      offer: '5% Cashback',
      price: '$50',
      discount: '5% OFF',
      groupSize: '18/25 Joined',
      desc: 'Prescription group savings',
      image: NICHE_IMAGES.healthcare[1],
      terms: 'Group prescription purchases unlock 5% cashback on all pharmacy items.',
    },
    {
      id: 'deal-6-3',
      groupId: 6,
      brand: 'Headspace',
      logo: 'HS',
      color: 'bg-orange-500',
      offer: '15% Cashback',
      price: '$13',
      discount: '15% OFF',
      groupSize: '8/12 Joined',
      desc: 'Mental wellness collective',
      image: NICHE_IMAGES.healthcare[0],
      terms: 'Group meditation app subscriptions for the HealthFam community.',
    },
    {
      id: 'deal-6-4',
      groupId: 6,
      brand: 'GoodRx',
      logo: 'GR',
      color: 'bg-yellow-500',
      offer: '8% Cashback',
      price: '$25',
      discount: '8% OFF',
      groupSize: '22/40 Joined',
      desc: 'Medication savings pool',
      image: NICHE_IMAGES.healthcare[1],
      terms: 'Stack GoodRx discounts with group cashback for maximum savings.',
    },
  ],
  7: [
    {
      id: 'deal-7-1',
      groupId: 7,
      brand: 'PlayStation',
      logo: 'PS',
      color: 'bg-blue-700',
      offer: '5% Cashback',
      price: '$70',
      discount: '5% OFF',
      groupSize: '12/15 Joined',
      desc: 'New release group buy',
      image: NICHE_IMAGES.gaming[0],
      terms: 'Pre-order upcoming hits with the squad. 5% back when the commit goal is reached.',
    },
    {
      id: 'deal-7-2',
      groupId: 7,
      brand: 'Xbox',
      logo: 'XB',
      color: 'bg-green-600',
      offer: '5% Cashback',
      price: '$60',
      discount: '5% OFF',
      groupSize: '9/12 Joined',
      desc: 'Game Pass ultimate deal',
      image: NICHE_IMAGES.gaming[1],
      terms: 'Group Game Pass subscriptions with shared rewards for all members.',
    },
    {
      id: 'deal-7-3',
      groupId: 7,
      brand: 'Steam',
      logo: 'ST',
      color: 'bg-slate-800',
      offer: '3% Cashback',
      price: '$50',
      discount: '3% OFF',
      groupSize: '25/30 Joined',
      desc: 'PC gaming collective',
      image: NICHE_IMAGES.gaming[0],
      terms: 'Steam wallet top-ups via Bonndle unlock group cashback rewards.',
    },
    {
      id: 'deal-7-4',
      groupId: 7,
      brand: 'Nintendo',
      logo: 'NT',
      color: 'bg-red-600',
      offer: '4% Cashback',
      price: '$60',
      discount: '4% OFF',
      groupSize: '7/10 Joined',
      desc: 'Switch games group buy',
      image: NICHE_IMAGES.gaming[1],
      terms: 'Coordinate Nintendo purchases with the GameOnn squad for rewards.',
    },
    {
      id: 'deal-7-5',
      groupId: 7,
      brand: 'Razer',
      logo: 'RZ',
      color: 'bg-lime-500',
      offer: '8% Cashback',
      price: '$150',
      discount: '8% OFF',
      groupSize: '4/8 Joined',
      desc: 'Gaming gear collective',
      image: NICHE_IMAGES.gaming[0],
      terms: 'Premium gaming peripherals with group discount stacking.',
    },
  ],
  8: [
    {
      id: 'deal-8-1',
      groupId: 8,
      brand: 'Udemy',
      logo: 'UD',
      color: 'bg-purple-700',
      offer: '20% Off',
      price: '$12',
      discount: '20% OFF',
      groupSize: '55/100 Joined',
      desc: 'Skill-up collective',
      image: NICHE_IMAGES.education[0],
      terms: 'Bulk course access for KAKUStudents members. Unlock the highest tier discount together.',
    },
    {
      id: 'deal-8-2',
      groupId: 8,
      brand: 'Coursera',
      logo: 'CR',
      color: 'bg-blue-600',
      offer: '15% Cashback',
      price: '$49',
      discount: '15% OFF',
      groupSize: '32/50 Joined',
      desc: 'Certificate courses',
      image: NICHE_IMAGES.education[1],
      terms: 'Group enrollment in professional certificates for career advancement.',
    },
    {
      id: 'deal-8-3',
      groupId: 8,
      brand: 'Skillshare',
      logo: 'SS',
      color: 'bg-teal-600',
      offer: '25% Off',
      price: '$14',
      discount: '25% OFF',
      groupSize: '18/25 Joined',
      desc: 'Creative skills group',
      image: NICHE_IMAGES.education[0],
      terms: 'Annual subscriptions with group creative learning benefits.',
    },
    {
      id: 'deal-8-4',
      groupId: 8,
      brand: 'LinkedIn Learning',
      logo: 'LL',
      color: 'bg-blue-800',
      offer: '10% Cashback',
      price: '$30',
      discount: '10% OFF',
      groupSize: '40/60 Joined',
      desc: 'Professional development',
      image: NICHE_IMAGES.education[1],
      terms: 'Business and tech skills training for the student collective.',
    },
    {
      id: 'deal-8-5',
      groupId: 8,
      brand: 'Chegg',
      logo: 'CG',
      color: 'bg-orange-600',
      offer: '12% Cashback',
      price: '$15',
      discount: '12% OFF',
      groupSize: '28/40 Joined',
      desc: 'Study tools collective',
      image: NICHE_IMAGES.education[0],
      terms: 'Textbook rentals and study help with group member benefits.',
    },
    {
      id: 'deal-8-6',
      groupId: 8,
      brand: 'Grammarly',
      logo: 'GR',
      color: 'bg-green-600',
      offer: '18% Off',
      price: '$12',
      discount: '18% OFF',
      groupSize: '45/70 Joined',
      desc: 'Writing tools group',
      image: NICHE_IMAGES.education[1],
      terms: 'Premium writing assistance for all KAKUStudents members.',
    },
  ],
  9: [
    {
      id: 'deal-9-1',
      groupId: 9,
      brand: 'Verizon',
      logo: 'VZ',
      color: 'bg-red-600',
      offer: '3% Cashback',
      price: '$80',
      discount: '4% OFF',
      groupSize: '12/20 committing',
      desc: 'Enterprise team data plan',
      image: NICHE_IMAGES.internet[0],
      terms: 'Internal payment flow: Pay your Verizon bill via Bonndle to secure team rewards.',
      category: 'internet',
    },
    {
      id: 'deal-9-2',
      groupId: 9,
      brand: 'HelloFresh',
      logo: 'HF',
      color: 'bg-lime-600',
      offer: '15% Cashback',
      price: '$65',
      discount: '15% OFF',
      groupSize: '4/5 committing',
      desc: 'Team health meals',
      image: NICHE_IMAGES.foodies[0],
      terms: 'Redirected checkout: Purchase on HelloFresh site to trigger team cashback.',
    },
    {
      id: 'deal-9-3',
      groupId: 9,
      brand: 'BetterHelp',
      logo: 'BH',
      color: 'bg-teal-700',
      offer: '10% Cashback',
      price: '$60',
      discount: '10% OFF',
      groupSize: '2/10 committing',
      desc: 'Wellness collective',
      image: NICHE_IMAGES.healthcare[1],
      terms: 'Redirected checkout: Professional wellness sessions for the MoneyMint team.',
    },
    {
      id: 'deal-9-4',
      groupId: 9,
      brand: 'Disney+',
      logo: 'DP',
      color: 'bg-blue-900',
      offer: '5% Cashback',
      price: '$14',
      discount: '5% OFF',
      groupSize: '8/10 committing',
      desc: 'Team movie night sub',
      image: NICHE_IMAGES.entertainment[0],
      terms: 'Redirected checkout: Shared subscription rewards for team entertainment.',
    },
  ],
};

// Initial notifications
export const INITIAL_NOTIFICATIONS = [
  {
    id: 1,
    title: 'New Deal Drop!',
    body: 'Nike has a new group buy active in #Tech Squad.',
    time: '2m ago',
    read: false,
    iconType: 'Zap',
    date: 'today',
  },
  {
    id: 2,
    title: 'Member Joined',
    body: 'Sarah B. just joined the #Weekend Foodies group.',
    time: '1h ago',
    read: false,
    iconType: 'Users',
    date: 'today',
  },
  {
    id: 3,
    title: 'Points Earned',
    body: 'You earned 450 Pts from your HelloFresh purchase.',
    time: '3h ago',
    read: true,
    iconType: 'Star',
    date: 'yesterday',
  },
  {
    id: 4,
    title: 'Group Goal Met',
    body: '#BillSavers reached the target! 5% cashback unlocked.',
    time: '1d ago',
    read: true,
    iconType: 'CheckCircle2',
    date: 'yesterday',
  },
];

// Initial feed data
export const INITIAL_FEED = [
  // Deals from groups user has joined (groupIds 1, 2, 3, 9)
  {
    id: 'd_vz',
    type: 'deal',
    author: 'Verizon',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=VZ',
    title: '3% Cashback on Verizon',
    content: 'Enterprise data rewards for the MoneyMint Team. Pay your bill in-app to unlock.',
    image: NICHE_IMAGES.internet[0],
    likes: 12,
    isLiked: false,
    date: 'Mar 29, 2026',
    dealBadge: '3% CASHBACK',
    comments: 8,
  },
  {
    id: 'd_hf',
    type: 'deal',
    author: 'HelloFresh',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=HF',
    title: 'Team Meal Win',
    content: '15% Cashback unlocked for MoneyMint members on all fresh meal kits.',
    image: NICHE_IMAGES.foodies[0],
    likes: 8,
    isLiked: false,
    date: 'Mar 28, 2026',
    dealBadge: '15% CASHBACK',
    comments: 5,
  },
  {
    id: 'd_wf',
    type: 'deal',
    author: 'Whole Foods',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=WF',
    title: '10% Cashback at Whole Foods',
    content: 'Weekly grocery haul with the Weekend Foodies. Unlock 10% collective cashback.',
    image: NICHE_IMAGES.foodies[1],
    likes: 928,
    isLiked: false,
    date: 'Mar 27, 2026',
    dealBadge: '10% CASHBACK',
    comments: 42,
  },
  // Deals from groups user hasn't joined yet
  {
    id: 'd_starlink',
    type: 'deal',
    author: 'Starlink',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=SL',
    title: 'Satellite Internet Collective',
    content: 'Join #Bonndle Internet to unlock 3% cashback on Starlink subscriptions.',
    image: NICHE_IMAGES.internet[1],
    likes: 156,
    isLiked: false,
    date: 'Mar 28, 2026',
    dealBadge: '3% CASHBACK',
    comments: 23,
  },
  {
    id: 'd_playstation',
    type: 'deal',
    author: 'PlayStation',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=PS',
    title: 'New Release Group Buy',
    content: 'Pre-order upcoming hits with #GameOnn squad. 5% back when the commit goal is reached.',
    image: NICHE_IMAGES.gaming[0],
    likes: 89,
    isLiked: false,
    date: 'Mar 26, 2026',
    dealBadge: '5% CASHBACK',
    comments: 31,
  },
  {
    id: 'd_udemy',
    type: 'deal',
    author: 'Udemy',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=UD',
    title: 'Skill-Up Collective',
    content: 'Bulk course access for #KAKUStudents members. Unlock the highest tier discount together.',
    image: NICHE_IMAGES.education[0],
    likes: 234,
    isLiked: false,
    date: 'Mar 25, 2026',
    dealBadge: '20% OFF',
    comments: 18,
  },
  // Success stories from community members
  {
    id: 'story-1',
    type: 'story',
    author: 'Marcus T.',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Marcus',
    title: 'First $100 in Rewards!',
    content: 'Just hit my first $100 in cashback rewards! The BillSavers group has been amazing for utility bill savings.',
    image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=500&q=80',
    likes: 452,
    isLiked: false,
    date: 'Mar 24, 2026',
    comments: 67,
  },
  {
    id: 'story-2',
    type: 'story',
    author: 'Jessica L.',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Jessica',
    title: 'Pro Tip: Stack Your Deals',
    content: "I've found that timing your purchases with group commits can double your savings. Wait for the threshold to hit!",
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=500&q=80',
    likes: 328,
    isLiked: false,
    date: 'Mar 22, 2026',
    comments: 45,
  },
  {
    id: 'insight-1',
    type: 'insight',
    author: 'Bonndle Analyst',
    avatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=Analyst',
    title: 'Collective Buying Power',
    content: 'Users shopping in groups save an average of 18% more than solo shoppers.',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=500&q=80',
    likes: 342,
    isLiked: false,
    date: 'Mar 20, 2026',
    comments: 89,
  },
  {
    id: 'story-3',
    type: 'story',
    author: 'David K.',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=David',
    title: 'Group Coordination Win',
    content: 'Our Tech Squad coordinated a bulk Apple order and everyone got 3% back. $45 saved just by shopping together!',
    image: NICHE_IMAGES.tech[0],
    likes: 567,
    isLiked: false,
    date: 'Mar 18, 2026',
    comments: 72,
  },
  {
    id: 'story-4',
    type: 'story',
    author: 'Amira S.',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Amira',
    title: 'Healthcare Savings Tip',
    content: "If you're not in HealthFam yet, join now! The Teladoc group deal just saved me $50 on telehealth visits.",
    image: NICHE_IMAGES.healthcare[0],
    likes: 189,
    isLiked: false,
    date: 'Mar 15, 2026',
    comments: 28,
  },
];

// Rewards data
export const CURRENT_PTS = '1,240,500';
export const CURRENT_USD = '$1,240.50';

// FAQ data
export const FAQ_DATA = [
  {
    q: 'What is Bonndle?',
    a: 'Bonndle is a collective buying platform that helps you save money by coordinating purchases with your community. Join groups, unlock shared rewards, and save together.',
  },
  {
    q: 'How do I earn cashback?',
    a: 'Earn cashback by participating in group deals. When enough members commit to a deal, everyone unlocks the cashback reward. Pay through the Bonndle app to trigger rewards.',
  },
  {
    q: 'How do I join a group?',
    a: "Browse available groups in the Groups tab, tap on one you're interested in, and click 'Join Group'. You can join public groups instantly or request to join private groups.",
  },
  {
    q: 'How do rewards work?',
    a: 'Rewards are earned as points (100 points = $0.10 USD). You can redeem them for discounts on future purchases or transfer to your linked payment method.',
  },
  {
    q: 'Can I create my own group?',
    a: 'Yes! Tap the + button in the Groups tab to create a new group. You can make it public or private and invite members.',
  },
  {
    q: 'How do I add a payment method?',
    a: "Go to Menu > Payment & Wallet > Add Card. We support Visa, Mastercard, and other major cards. You can also enable 'Pay with Rewards' to automatically apply your points.",
  },
  {
    q: 'Is my payment information secure?',
    a: 'Yes, we use bank-level encryption and never store your full card details. All payments are processed through secure, PCI-compliant providers.',
  },
  {
    q: 'How do I contact support?',
    a: 'Go to Menu > Support Center where you can chat with our AI assistant, browse FAQs, or send an email to our support team.',
  },
];
